// Pure helpers extracted from building-capture-screen.tsx to keep
// that file under the max-lines policy.

import type { SpaceWithItems } from '@citycare/shared'

export interface OrderedFloor {
  id: string
  name: string
  spaces: SpaceWithItems[]
}

interface BuildingFloorRef {
  id: string
  name: string
  displayOrder: number
}

// §5.4a — floors sorted by displayOrder asc, then name. Floors that
// appear in spaces but not in `building.floors` (data drift) are
// appended in first-seen order.
export function orderFloors(
  buildingFloors: readonly BuildingFloorRef[],
  spaces: readonly SpaceWithItems[],
): OrderedFloor[] {
  const floorMap = new Map<string, { name: string; spaces: SpaceWithItems[] }>()
  for (const space of spaces) {
    if (space.isPseudo || space.floorId === null) continue
    const entry = floorMap.get(space.floorId) ?? {
      name: space.floorName ?? 'Unknown Floor',
      spaces: [],
    }
    entry.spaces.push(space)
    floorMap.set(space.floorId, entry)
  }
  const seen = new Set<string>()
  const ordered: OrderedFloor[] = [...buildingFloors]
    .sort((a, b) =>
      a.displayOrder !== b.displayOrder
        ? a.displayOrder - b.displayOrder
        : a.name.localeCompare(b.name),
    )
    .flatMap((f) => {
      seen.add(f.id)
      const entry = floorMap.get(f.id)
      return entry !== undefined && entry.spaces.length > 0
        ? [{ id: f.id, name: f.name, spaces: entry.spaces }]
        : []
    })
  for (const [id, { name, spaces: list }] of floorMap) {
    if (!seen.has(id)) ordered.push({ id, name, spaces: list })
  }
  return ordered
}

// Aggregate done/total counts across all spaces — drives the hero
// subtitle ("X/Y assets captured"). "Done" matches the field UI
// definition: any positive quantity OR an explicit zero confirmation.
export function aggregateAssetCounts(
  spaces: readonly SpaceWithItems[],
): { totalAssets: number; doneAssets: number } {
  let totalAssets = 0
  let doneAssets = 0
  for (const space of spaces) {
    for (const item of space.items) {
      totalAssets += 1
      if (parseFloat(item.quantity) > 0 || item.isZeroConfirmed) {
        doneAssets += 1
      }
    }
  }
  return { totalAssets, doneAssets }
}

// First incomplete room — sticky-bar "Continue capture" target.
// Skips zero-asset rooms (they have no completion bar).
export function findContinueTarget(
  ordered: readonly OrderedFloor[],
): SpaceWithItems | undefined {
  return ordered
    .flatMap((f) => f.spaces)
    .find((space) => {
      if (space.items.length === 0) return false
      const completed = space.items.filter(
        (i) => parseFloat(i.quantity) > 0 || i.isZeroConfirmed,
      ).length
      return completed < space.items.length
    })
}
